import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, LogOut, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ active: false });
  const [now, setNow] = useState(Date.now());
  const [exiting, setExiting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/impersonate/status`);
      setStatus(data || { active: false });
    } catch {
      setStatus({ active: false });
    }
  }, []);

  useEffect(() => {
    refresh();
    // Re-poll every 30 s in case the cookie expires while the page is open
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    if (!status.active) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [status.active]);

  const endImpersonation = async () => {
    setExiting(true);
    try {
      await axios.post(`${API}/admin/impersonate/end`);
      toast.success(t('admin.impersonation.ended'));
      // Force a full reload — the user identity has changed server-side, so
      // every cached query/component should refetch as the original admin.
      window.location.reload();
    } catch {
      toast.error(t('admin.impersonation.endFailed'));
      setExiting(false);
    }
  };

  if (!status.active) return null;

  const remainingSeconds = Math.max(0, Math.floor((status.expires_at || 0) - now / 1000));
  const mm = String(Math.floor(remainingSeconds / 60)).padStart(2, '0');
  const ss = String(remainingSeconds % 60).padStart(2, '0');
  const expiringSoon = remainingSeconds < 60;

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[100] bg-destructive text-destructive-foreground shadow-md border-b border-destructive/40"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <p className="text-sm font-medium">
            {t('admin.impersonation.actingAs')}{' '}
            <span className="font-mono font-semibold">{status.target_email || status.target_user_id}</span>
            {status.target_role && (
              <span className="ml-1 opacity-80">({status.target_role})</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span
            className={`flex items-center gap-1 text-xs font-mono ${expiringSoon ? 'animate-pulse' : ''}`}
            data-testid="impersonation-banner-timer"
          >
            <Clock className="w-3.5 h-3.5" />
            {mm}:{ss}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={endImpersonation}
            disabled={exiting}
            data-testid="impersonation-banner-exit"
            className="bg-white text-destructive hover:bg-white/90"
          >
            <LogOut className="w-3.5 h-3.5 mr-1" />
            {t('admin.impersonation.exit')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
